// Kopplar profiler som aktiverat sig via hyria.se till lägenhetsraderna i
// medlemsförteckningen. Bara visning: inget skrivs.
// Normaliseringen speglar src/api/housing.ts.

const normalizeStreet = (s) => (s || '')
  .replace(/,\s*[^,]+$/, '')
  .replace(/,\s*lgh\s*$/i, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();
const normalizeApartment = (s) => (s || '').replace(/lgh\s*/gi, '').replace(/\s+/g, '').trim().toLowerCase();
const normalizeZip = (s) => (s || '').replace(/\s+/g, '').trim();

export function activationKey({ address, zipCode, apartmentNumber }) {
  return `${normalizeStreet(address)}|${normalizeZip(zipCode)}|${normalizeApartment(apartmentNumber)}`;
}

export function isActivatedProfile(user) {
  return Boolean(user && user.activation && user.activation.at);
}

const sameName = (member, profile) =>
  Boolean(member && profile) &&
  (member.firstName || '').trim().toLowerCase() === (profile.firstName || '').trim().toLowerCase() &&
  (member.lastName || '').trim().toLowerCase() === (profile.lastName || '').trim().toLowerCase();

/**
 * rows: rader som i Units.jsx/AssociationDetail.jsx ({ id, memberIndex, member, address, zipCode, apartmentNumber })
 * profiles: users-dokument med id
 * Returnerar nya rader (matchade får `activated`) och de profiler som inte
 * kunde knytas till exakt ett lägenhetsdokument.
 */
export function mergeActivatedMembers(rows, profiles) {
  const out = (rows || []).map((r) => ({ ...r }));
  const indexByKey = new Map();
  out.forEach((r, i) => {
    const key = activationKey(r);
    if (!indexByKey.has(key)) indexByKey.set(key, []);
    indexByKey.get(key).push(i);
  });

  const unmatched = [];
  (profiles || []).filter(isActivatedProfile).forEach((p) => {
    const indices = indexByKey.get(activationKey(p)) || [];
    const docIds = new Set(indices.map((i) => out[i].id));
    if (docIds.size !== 1) {
      unmatched.push(p);
      return;
    }
    const byName = indices.find((i) => sameName(out[i].member, p));
    const target = byName !== undefined ? byName : indices[0];
    out[target] = { ...out[target], activated: p };
  });

  return { rows: out, unmatched };
}

/**
 * Tomma lägenhetsrader som bara är dubbletter (Trello 432). Ett hushåll kan ha
 * två poster för samma lägenhet; tar Hyria bort medlemmen ur den ena blir den
 * kvar som "Lägenhet utan registrerad medlem", fast lägenheten har en medlem på
 * den andra. Den tomma raden döljs då. En lägenhet som verkligen saknar medlem
 * visas som förut, och utan lägenhetsnummer jämförs inget. Bara visning.
 */
export function withoutEmptyDuplicates(rows) {
  const list = rows || [];
  const hasApartment = (r) => normalizeApartment(r.apartmentNumber) !== '';
  const withMember = new Set(
    list.filter((r) => (!r.empty || r.activated) && hasApartment(r)).map(activationKey),
  );
  return list.filter((r) => !(r.empty && !r.activated && hasApartment(r) && withMember.has(activationKey(r))));
}
